import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SearchableSelect from "@/components/SearchableSelect";
import ProjectPartBreakdown from "@/components/ProjectPartBreakdown";

// Cost Breakdown (sidebar → Costing → below Assemblies). Deliberately lean: it is just the
// Part Cost Breakdown list — one column-header row in front of the parts, and each part opens
// its full costing detail inline (dropdown), NOT a separate full page per part.
export default function CostBreakdown() {
  const { user, hasPermission } = useAuth();
  const canView = user?.role === "admin" || hasPermission("view_cost_breakdown");
  const hideMargin = user?.role === "npd_engineer";
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  // Pre-filter to a project when arrived via ?project=<id> (e.g. the "Cost Breakdown"
  // button on a project page), so that project's part breakdown opens highlighted.
  const [projectFilter, setProjectFilter] = useState(params.get("project") || "");
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  // Keep the URL (?project=) in sync with the picker so the link is shareable/back-safe.
  useEffect(() => {
    const cur = params.get("project") || "";
    if (cur !== projectFilter) {
      const next = new URLSearchParams(params);
      if (projectFilter) next.set("project", projectFilter); else next.delete("project");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500, exclude_assembly_children: false };
      if (q) params.search = q;
      // When a project is picked, ask the backend for ONLY that project's parts.
      if (projectFilter) params.project_id = projectFilter;
      const r = await api.get("/npd", { params });
      setRows(r.data.npds || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Load the project list once for the dropdown.
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data.projects || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, projectFilter]);

  const projectOptions = [
    { value: "", label: "All projects" },
    ...projects
      .slice()
      .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))
      .map((p) => ({ value: p.project_id, label: p.project_name || p.project_number })),
  ];

  if (!canView) {
    return (
      <div className="p-6 lg:p-8 max-w-xl mx-auto text-center" data-testid="cost-breakdown-no-access">
        <div className="mt-16 rounded-xl border border-slate-200 bg-white p-8">
          <h2 className="text-lg font-semibold text-slate-900">No access to Cost Breakdown</h2>
          <p className="mt-2 text-sm text-slate-500">Your role does not have the <span className="font-mono">view_cost_breakdown</span> permission. Ask an admin to enable it under Settings → Roles &amp; Permissions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-[1700px] mx-auto" data-testid="cost-breakdown-page">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Back to the project's costing page — shown when filtered to a project (e.g.
            arrived here via the project's "Cost Breakdown" button). */}
        {projectFilter && (
          <button type="button" onClick={() => nav(`/projects/${projectFilter}`)} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-indigo-300 text-indigo-700 rounded-md hover:bg-indigo-50" data-testid="cb-back-costing">
            <ArrowLeft size={15} /> Back to costing
          </button>
        )}
        {/* Back to the full (all-projects) breakdown when a project is selected */}
        {projectFilter && (
          <button type="button" onClick={() => setProjectFilter("")} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50" data-testid="cb-back-all">
            <ArrowLeft size={15} /> All projects
          </button>
        )}
        {/* Project selector — pick a project to see only its parts. Styled like the part search. */}
        <div className="relative w-full sm:w-80" data-testid="cb-project-filter">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10 pointer-events-none" />
          <SearchableSelect
            value={projectFilter}
            onChange={setProjectFilter}
            options={projectOptions}
            placeholder="All projects — pick one to filter"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-orange-300"
            testid="cb-project-select"
          />
        </div>
        {/* Part search */}
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search part no. / name / NPD #"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            data-testid="cb-search"
          />
        </div>
      </div>

      {!loading && !projectFilter && rows.length >= 500 && (
        <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Showing the first 500 parts. Pick a project above or use the search box to narrow down.
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-sm text-slate-400">Loading cost breakdown…</div>
      ) : rows.length === 0 && !projectFilter ? (
        <div className="text-center py-16 text-sm text-slate-400">No costings match your search.</div>
      ) : (
        <ProjectPartBreakdown
          parts={rows}
          hideMargin={hideMargin}
          projectId={projectFilter || undefined}
          customerId={projects.find((p) => p.project_id === projectFilter)?.customer_id}
          onReload={load}
        />
      )}
    </div>
  );
}
